/**
 * MealCalculatorModal.jsx
 * KI-Mahlzeit-Kalkulator - Berechnet Portionen basierend auf verfügbaren Zutaten
 */
import React, { useState, useEffect } from 'react';
import { X, Calculator, Sparkles, Plus, Trash2, ChefHat, RefreshCw, Check, Settings } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { sortFavoritesByPinnedOrder } from '@/utils/favoritesOrder';
import { useTranslation } from 'react-i18next';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export const MealCalculatorModal = ({ 
  isOpen, 
  onClose, 
  portalToken, 
  customerId,
  onMealCalculated,
  remainingCalories,
  remainingProtein,
  remainingCarbs,
  remainingFat
}) => {
  const [ingredients, setIngredients] = useState([]);
  const [newIngredient, setNewIngredient] = useState('');
  const [mealType, setMealType] = useState('dinner');
  const [calculating, setCalculating] = useState(false);
  const [result, setResult] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [mealDistribution, setMealDistribution] = useState({
    breakfast: 25,
    lunch: 35,
    dinner: 30,
    snacks: 10
  });
  const [savingSettings, setSavingSettings] = useState(false);
  
  // Favorite ingredients and history for quick selection
  const [favorites, setFavorites] = useState([]);
  const [frequentFoods, setFrequentFoods] = useState([]);
  const [recentMeals, setRecentMeals] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  
  // Load favorites, history and meal distribution on open
  useEffect(() => {
    if (isOpen && portalToken && customerId) {
      loadFavorites();
      loadMealDistribution();
      loadSmartSuggestions();
    }
  }, [isOpen, portalToken, customerId, mealType]);
  
  const loadFavorites = async () => {
    try {
      const response = await axios.get(
        `${API}/meal-assistant/favorite-foods/${customerId}`,
        { headers: { Authorization: `Bearer ${portalToken}` } }
      );
      const foods = response.data?.favorites || response.data?.foods || [];
      setFavorites(sortFavoritesByPinnedOrder(foods));
    } catch (err) {
      console.error('Load favorites error:', err);
    }
  };
  
  const loadSmartSuggestions = async () => {
    setLoadingHistory(true);
    try {
      const response = await axios.get(
        `${API}/meal-assistant/smart-suggestions/${customerId}?meal_type=${mealType}`,
        { headers: { Authorization: `Bearer ${portalToken}` } }
      );
      
      const data = response.data;
      
      // Recent meals from history
      if (data.recent_favorites?.length > 0) {
        setRecentMeals(data.recent_favorites.map(f => ({ food_name: f.name, count: f.count_this_week })));
      }
      
      // Frequently eaten foods (use frequently_eaten or smart_suggestions)
      const frequent = data.frequently_eaten || data.smart_suggestions || [];
      if (frequent.length > 0) {
        setFrequentFoods(frequent.map(f => ({ food_name: f.name, count: f.count || 1 })));
      }
      
      // Meal type specific suggestions
      if (data.meal_type_suggestions?.[mealType]?.length > 0) {
        const mealSpecific = data.meal_type_suggestions[mealType];
        setFrequentFoods(prev => {
          const merged = [...prev];
          mealSpecific.forEach(s => {
            if (!merged.find(f => f.food_name === s.name)) {
              merged.push({ food_name: s.name, count: s.count || 1 });
            }
          });
          return merged.slice(0, 12);
        });
      }
    } catch (err) {
      console.error('Load smart suggestions error:', err);
    } finally {
      setLoadingHistory(false);
    }
  };
  
  const loadMealDistribution = async () => {
    try {
      const response = await axios.get(
        `${API}/meal-assistant/meal-distribution/${customerId}`,
        { headers: { Authorization: `Bearer ${portalToken}` } }
      );
      if (response.data.meal_distribution) {
        setMealDistribution(response.data.meal_distribution);
      }
    } catch (err) {
      console.error('Load meal distribution error:', err);
    }
  };
  
  const saveMealDistribution = async () => {
    // Validate adds up to 100
    const total = Object.values(mealDistribution).reduce((a, b) => a + b, 0);
    if (Math.abs(total - 100) > 1) {
      toast.error(`Verteilung muss 100% ergeben (aktuell: ${total}%)`);
      return;
    }
    
    setSavingSettings(true);
    try {
      await axios.put(
        `${API}/meal-assistant/meal-distribution/${customerId}`,
        mealDistribution,
        { headers: { Authorization: `Bearer ${portalToken}` } }
      );
      toast.success('Einstellungen gespeichert');
      setShowSettings(false);
    } catch (err) {
      toast.error('Fehler beim Speichern');
    } finally {
      setSavingSettings(false);
    }
  };
  
  const addIngredient = () => {
    if (newIngredient.trim() && !ingredients.includes(newIngredient.trim())) {
      setIngredients([...ingredients, newIngredient.trim()]);
      setNewIngredient('');
    }
  };
  
  const addFavoriteIngredient = (fav) => {
    const name = fav.name || fav.food_name;
    if (!ingredients.includes(name)) {
      setIngredients([...ingredients, name]);
    }
  };
  
  const removeIngredient = (index) => {
    setIngredients(ingredients.filter((_, i) => i !== index));
  };
  
  const calculatePortions = async () => {
    if (ingredients.length === 0) {
      toast.error('Bitte mindestens eine Zutat hinzufügen');
      return;
    }
    
    setCalculating(true);
    setResult(null);
    
    try {
      const response = await axios.post(
        `${API}/meal-assistant/calculate-portions`,
        {
          customer_id: customerId,
          ingredients: ingredients,
          meal_type: mealType,
          remaining_calories: remainingCalories > 0 ? Math.round(remainingCalories) : null,
          remaining_protein: remainingProtein >= 0 && remainingCalories > 0 ? Math.round(remainingProtein) : null,
          remaining_carbs: remainingCarbs >= 0 && remainingCalories > 0 ? Math.round(remainingCarbs) : null,
          remaining_fat: remainingFat >= 0 && remainingCalories > 0 ? Math.round(remainingFat) : null
        },
        { 
          headers: { Authorization: `Bearer ${portalToken}` },
          timeout: 30000
        }
      );
      
      setResult(response.data);
      toast.success('Berechnung abgeschlossen!');
    } catch (err) {
      console.error('Calculate error:', err);
      toast.error(err.response?.data?.detail || 'Berechnung fehlgeschlagen');
    } finally {
      setCalculating(false);
    }
  };
  
  const applyResult = () => {
    if (result && onMealCalculated) {
      onMealCalculated(result);
      resetAndClose();
    }
  };
  
  const resetAndClose = () => {
    setIngredients([]);
    setNewIngredient('');
    setResult(null);
    setShowSettings(false);
    onClose();
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="absolute inset-0 bg-black/95 z-50 flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-zinc-700">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={resetAndClose} className="p-2 bg-zinc-800 rounded-lg">
              <X className="w-5 h-5 text-zinc-400" />
            </button>
            <div>
              <h2 className="text-white font-bold">KI-Mahlzeit-Kalkulator</h2>
              <p className="text-zinc-500 text-xs">Berechne optimale Portionen</p>
            </div>
          </div>
          <button 
            onClick={() => setShowSettings(!showSettings)}
            className={`p-2 rounded-lg ${showSettings ? 'bg-yellow-500/20 text-yellow-400' : 'bg-zinc-800 text-zinc-400'}`}
          >
            <Settings className="w-5 h-5" />
          </button>
        </div>
      </div>
      
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {/* Settings Panel */}
        {showSettings && (
          <div className="bg-zinc-800/50 rounded-xl p-4 border border-zinc-700">
            <h3 className="text-white font-medium mb-3 flex items-center gap-2">
              <ChefHat className="w-4 h-4 text-yellow-400" />
              Mahlzeit-Verteilung
            </h3>
            <p className="text-zinc-500 text-xs mb-4">
              Wie viel % deiner täglichen Kalorien pro Mahlzeit?
            </p>
            
            <div className="space-y-3">
              {[
                { key: 'breakfast', label: 'Frühstück', emoji: '🌅' },
                { key: 'lunch', label: 'Mittagessen', emoji: '☀️' },
                { key: 'dinner', label: 'Abendessen', emoji: '🌙' },
                { key: 'snacks', label: 'Snacks', emoji: '🍎' }
              ].map(({ key, label, emoji }) => (
                <div key={key} className="flex items-center gap-3">
                  <span className="text-lg">{emoji}</span>
                  <span className="text-zinc-300 text-sm flex-1">{label}</span>
                  <div className="flex items-center gap-2">
                    <input
                      type="range"
                      min="0"
                      max="50"
                      value={mealDistribution[key]}
                      onChange={(e) => setMealDistribution({
                        ...mealDistribution,
                        [key]: parseInt(e.target.value)
                      })}
                      className="w-20 accent-yellow-500"
                    />
                    <span className="text-yellow-400 font-medium w-10 text-right">
                      {mealDistribution[key]}%
                    </span>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="mt-4 pt-3 border-t border-zinc-700">
              <div className="flex items-center justify-between">
                <span className="text-zinc-400 text-sm">
                  Total: {Object.values(mealDistribution).reduce((a, b) => a + b, 0)}%
                </span>
                <button
                  onClick={saveMealDistribution}
                  disabled={savingSettings}
                  className="px-4 py-2 bg-yellow-500 text-black rounded-lg font-medium text-sm disabled:opacity-50"
                >
                  {savingSettings ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Speichern'}
                </button>
              </div>
            </div>
          </div>
        )}
        
        {/* Meal Type Selection */}
        <div>
          <label className="text-zinc-400 text-xs uppercase tracking-wide mb-2 block">
            Mahlzeit-Typ
          </label>
          <div className="grid grid-cols-4 gap-2">
            {[
              { key: 'breakfast', label: 'Frühstück', emoji: '🌅' },
              { key: 'lunch', label: 'Mittag', emoji: '☀️' },
              { key: 'dinner', label: 'Abend', emoji: '🌙' },
              { key: 'snack', label: 'Snack', emoji: '🍎' }
            ].map(({ key, label, emoji }) => (
              <button
                key={key}
                onClick={() => setMealType(key)}
                className={`p-3 rounded-xl text-center transition-all ${
                  mealType === key
                    ? 'bg-yellow-500/20 border-2 border-yellow-500'
                    : 'bg-zinc-800/50 border-2 border-transparent'
                }`}
              >
                <span className="text-xl block">{emoji}</span>
                <span className={`text-xs ${mealType === key ? 'text-yellow-400' : 'text-zinc-400'}`}>
                  {label}
                </span>
              </button>
            ))}
          </div>
        </div>
        
        {/* Remaining Macros Info */}
        {remainingCalories !== undefined && (
          <div className="bg-gradient-to-r from-yellow-500/10 to-orange-500/10 rounded-xl p-3 border border-yellow-500/20">
            <p className="text-yellow-400 text-xs font-medium mb-2">Verbleibende Ziele:</p>
            <div className="grid grid-cols-4 gap-2 text-center">
              <div>
                <p className="text-white font-bold">{remainingCalories}</p>
                <p className="text-zinc-500 text-xs">kcal</p>
              </div>
              <div>
                <p className="text-blue-400 font-bold">{remainingProtein?.toFixed(0) || '-'}g</p>
                <p className="text-zinc-500 text-xs">Protein</p>
              </div>
              <div>
                <p className="text-green-400 font-bold">{remainingCarbs?.toFixed(0) || '-'}g</p>
                <p className="text-zinc-500 text-xs">Carbs</p>
              </div>
              <div>
                <p className="text-orange-400 font-bold">{remainingFat?.toFixed(0) || '-'}g</p>
                <p className="text-zinc-500 text-xs">Fett</p>
              </div>
            </div>
          </div>
        )}
        
        {/* Ingredient Input */}
        <div>
          <label className="text-zinc-400 text-xs uppercase tracking-wide mb-2 block">
            Verfügbare Zutaten
          </label>
          <div className="flex gap-2">
            <input
              type="text"
              value={newIngredient}
              onChange={(e) => setNewIngredient(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && addIngredient()}
              placeholder="z.B. Reis, Poulet..."
              className="flex-1 bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm placeholder-zinc-500"
            />
            <button
              onClick={addIngredient}
              disabled={!newIngredient.trim()}
              className="px-4 py-2 bg-yellow-500 text-black rounded-lg font-medium disabled:opacity-50"
            >
              <Plus className="w-5 h-5" />
            </button>
          </div>
        </div>
        
        {/* Quick Add Sections - Favorites, Frequent Foods & Recent Meals */}
        <div className="space-y-3">
          {/* Favorites */}
          {favorites.length > 0 && (
            <div>
              <label className="text-zinc-400 text-xs uppercase tracking-wide mb-2 flex items-center gap-1 block">
                ⭐ Favoriten
              </label>
              <div className="flex flex-wrap gap-2">
                {favorites.slice(0, 6).map((fav, i) => {
                  const name = fav.name || fav.food_name;
                  const isSelected = ingredients.includes(name);
                  return (
                    <button
                      key={`fav-${i}`}
                      onClick={() => !isSelected && setIngredients([...ingredients, name])}
                      disabled={isSelected}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                        isSelected
                          ? 'bg-zinc-700 text-zinc-500'
                          : 'bg-yellow-500/10 border border-yellow-500/30 text-yellow-400 hover:bg-yellow-500/20'
                      }`}
                    >
                      {name}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          
          {/* Frequent Foods from History */}
          {frequentFoods.length > 0 && (
            <div>
              <label className="text-zinc-400 text-xs uppercase tracking-wide mb-2 flex items-center gap-1 block">
                🔄 Häufig verwendet
              </label>
              <div className="flex flex-wrap gap-2">
                {frequentFoods.slice(0, 8).map((food, i) => {
                  const name = food.food_name || food.name;
                  const isSelected = ingredients.includes(name);
                  return (
                    <button
                      key={`freq-${i}`}
                      onClick={() => !isSelected && setIngredients([...ingredients, name])}
                      disabled={isSelected}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                        isSelected
                          ? 'bg-zinc-700 text-zinc-500'
                          : 'bg-blue-500/10 border border-blue-500/30 text-blue-400 hover:bg-blue-500/20'
                      }`}
                    >
                      {name}
                      {food.count > 1 && <span className="ml-1 opacity-60">({food.count}x)</span>}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          
          {/* Recent Meals */}
          {recentMeals.length > 0 && (
            <div>
              <label className="text-zinc-400 text-xs uppercase tracking-wide mb-2 flex items-center gap-1 block">
                📅 Letzte Mahlzeiten
              </label>
              <div className="flex flex-wrap gap-2">
                {recentMeals.slice(0, 5).map((meal, i) => {
                  const name = meal.food_name || meal.name;
                  const isSelected = ingredients.includes(name);
                  return (
                    <button
                      key={`recent-${i}`}
                      onClick={() => !isSelected && setIngredients([...ingredients, name])}
                      disabled={isSelected}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                        isSelected
                          ? 'bg-zinc-700 text-zinc-500'
                          : 'bg-green-500/10 border border-green-500/30 text-green-400 hover:bg-green-500/20'
                      }`}
                    >
                      {name}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          
          {/* Loading */}
          {loadingHistory && frequentFoods.length === 0 && favorites.length === 0 && (
            <div className="text-center py-2 text-zinc-500 text-xs">
              <RefreshCw className="w-4 h-4 animate-spin inline mr-2" />
              Lade Vorschläge...
            </div>
          )}
          
          {/* Empty State */}
          {!loadingHistory && frequentFoods.length === 0 && favorites.length === 0 && recentMeals.length === 0 && (
            <div className="text-center py-3 px-4 text-xs text-zinc-500 bg-zinc-800/50 rounded-lg">
              💡 Tipp: Je mehr Mahlzeiten du erfasst, desto besser werden die Vorschläge!
            </div>
          )}
        </div>
        
        {/* Selected Ingredients */}
        {ingredients.length > 0 && (
          <div>
            <label className="text-zinc-400 text-xs uppercase tracking-wide mb-2 block">
              Ausgewählt ({ingredients.length})
            </label>
            <div className="flex flex-wrap gap-2">
              {ingredients.map((ing, i) => (
                <span
                  key={i}
                  className="inline-flex items-center gap-2 px-3 py-1.5 bg-yellow-500/20 border border-yellow-500/30 rounded-full text-yellow-400 text-sm"
                >
                  {ing}
                  <button onClick={() => removeIngredient(i)} className="hover:text-red-400">
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
          </div>
        )}
        
        {/* Calculate Button */}
        <button
          onClick={calculatePortions}
          disabled={calculating || ingredients.length === 0}
          className="w-full py-4 bg-gradient-to-r from-yellow-500 to-orange-500 text-black rounded-xl font-bold flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {calculating ? (
            <>
              <RefreshCw className="w-5 h-5 animate-spin" />
              Berechne...
            </>
          ) : (
            <>
              <Sparkles className="w-5 h-5" />
              Portionen berechnen
            </>
          )}
        </button>
        
        {/* Result */}
        {result && result.portions && (
          <div className="bg-zinc-800/50 rounded-xl p-4 border border-green-500/30">
            <div className="flex items-center gap-2 mb-4">
              <Check className="w-5 h-5 text-green-400" />
              <h3 className="text-white font-bold">Empfohlene Portionen</h3>
            </div>
            
            <div className="space-y-3">
              {result.portions.map((portion, i) => (
                <div key={i} className="flex items-center justify-between p-3 bg-zinc-900/50 rounded-lg">
                  <div>
                    <p className="text-white font-medium">{portion.ingredient}</p>
                    <p className="text-zinc-500 text-xs">
                      {Math.round(portion.calories || 0)} kcal • {portion.protein}g P • {portion.carbs}g K • {portion.fat}g F
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-yellow-400 font-bold text-lg">{portion.grams}g</p>
                  </div>
                </div>
              ))}
            </div>
            
            {/* Totals */}
            <div className="mt-4 pt-4 border-t border-zinc-700">
              <div className="flex justify-between items-center">
                <span className="text-zinc-400">Gesamt:</span>
                <div className="text-right">
                  <p className="text-white font-bold">{Math.round(result.totals?.calories || 0)} kcal</p>
                  <p className="text-zinc-400 text-xs">
                    P: {result.totals?.protein}g • K: {result.totals?.carbs}g • F: {result.totals?.fat}g
                  </p>
                </div>
              </div>
            </div>
            
            {/* Tips */}
            {result.tips && (
              <div className="mt-3 p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                <p className="text-blue-400 text-sm">💡 {result.tips}</p>
              </div>
            )}
            
            {/* Apply Button */}
            <button
              onClick={applyResult}
              className="w-full mt-4 py-3 bg-green-500 text-white rounded-xl font-bold flex items-center justify-center gap-2"
            >
              <Check className="w-5 h-5" />
              Mahlzeit übernehmen
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default MealCalculatorModal;
